import { AuthConstantS, AuthTypeS } from '@/features/Auth/outbound/share';
import { SnsApiS, SnsSchemaS } from '@/features/Sns/outbound/share';
import { APP_STORE_LINK, VALID_APP_VERSION } from '@/libs/constant/AppConstant';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { T } from '@/libs/translation';
import { compareAppVersion } from '@/libs/util/compareAppVersion';
import { ButtonSnsIcon, Link } from '@/ui/component/atom';
import { Modal, Toast } from '@/ui/component/cell';
import { SnsDisconnectEvent } from '@/ui/component/event';
import { SnsDisconnectModal } from '@/ui/component/view';
import { getSharedData } from '@/ui/util';
import { map, pipe, toArray, values } from '@fxts/core';
import { mauth } from '@marpple/auth';
import { html, View } from 'rune-ts';
import { makeErrorPopup } from '../Popup/makeErrorPopup';
import { makePopup } from '../Popup/makePopup';
import klass from './SnsList.module.scss';

export class SnsList extends View<{ user: AuthTypeS.UserProfileType & { hasPassword?: boolean } }> {
  override template() {
    const { user } = this.data;
    const { client_id } = getSharedData(this);

    const {
      $many: { user_sns_informations: user_sns_infos },
    } = user;

    const sns_list = pipe(
      AuthConstantS.USER_LOGIN_SNS_TYPE_MAPPER,
      values,
      map((type) => {
        const user_sns = user_sns_infos.find((info) => info.sns_type === type);

        if (user_sns) {
          return html`${new ButtonSnsIcon({ type }, { active: true, hasUnlinkIcon: true })}`;
        }

        return html`${new Link(
          {
            label: new ButtonSnsIcon({ type }, { active: false }),
            href: `/oauth/request/${type.toLocaleLowerCase()}?type=connection&client_id=${client_id}`,
          },
          { klass: 'sns_login_link' },
        )}`;
      }),
      toArray,
    );
    return html`<div class="${klass.button_group_sns}">${sns_list}</div>`;
  }

  override onRender() {
    const { app_info, theme } = getSharedData(this);
    this.delegate('click', '.sns_login_link', async (e) => {
      if (app_info && compareAppVersion(app_info.version, VALID_APP_VERSION[theme]) === -1) {
        e.originalEvent.preventDefault();

        const res = await makePopup({
          title: T('app.need_update.title'),
          message: T('app.need_update.message'),
          ok: T('app.need_update.ok'),
          cancel: T('common.cancel'),
        });

        if (res === 'confirm') {
          location.href = APP_STORE_LINK[theme][app_info.os];
        }
      }
    });

    this.delegate('click', `[data-active='true']`, async (e) => {
      try {
        const button = e.currentTarget as HTMLButtonElement;
        const sns_type = button.dataset.type! as AuthTypeS.USER_SNS_TYPE;

        // sns 단일 회원
        if (!this.data.user.hasPassword && this.data.user.$many.user_sns_informations.length === 1) {
          await Modal.open(
            {
              view: new SnsDisconnectModal({
                sns_type,
                email: this.data.user.email,
                nickname: this.data.user.nickname,
              }),
            },
            {
              header: {
                title: T('mypage.sns.disconnect.title.001'),
              },
              footer: {
                cancel: T('mypage.sns.disconnect.button.001'),
                confirm: T('mypage.sns.disconnect.button.002'),
                confirm_option: {
                  color: 'primary',
                  disabled: true,
                },
              },
              backdrop: true,
              append_to: this.parentView?.element(),
            },
          );
        } else {
          const res = await makePopup({
            title: T(`mypage.sns.disconnect.popup.title.001.${sns_type}`),
            message: T(`mypage.sns.disconnect.message.${sns_type.toLocaleLowerCase()}`),
            ok: T('mypage.sns.disconnect.button.002'),
            cancel: T('mypage.sns.disconnect.button.004'),
          });

          if (res === 'confirm') {
            const type = SnsSchemaS.SnsConnectDeleteControllerReqBody.parse({
              sns_type: button.dataset.type,
            });
            const { data } = await SnsApiS.deleteSnsConnect(type);
            this.data.user.$many.user_sns_informations = this.data.user.$many.user_sns_informations.filter(
              (info) => info.sns_type !== data.sns_type,
            );

            await mauth.refreshUser();
            Toast.open({ label: T('mypage.sns.disconnect.toast.001'), close_label: T('common.close') }, {});
            this.redraw();
          }
        }
      } catch (e) {
        if (isFeatureError(e)) {
          return makeErrorPopup(e, e.json);
        }
        return makeErrorPopup(e);
      }
    });

    this.addEventListener(SnsDisconnectEvent, (e) => {
      const { sns_type } = e.detail;
      this.data.user.$many.user_sns_informations = this.data.user.$many.user_sns_informations.filter(
        (info) => info.sns_type !== sns_type,
      );
      this.redraw();
    });
  }
}
